import {Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output} from '@angular/core';
import {ApiArticle, ApiArticlePrice} from '@iw-shop/api/articles';
import {ArticleService} from '@iw-shop/article/services/article.service';
import {combineLatest, Subject} from 'rxjs';
import {Store} from '@ngrx/store';
import {AppState} from '@store/state/app.state';
import {take, takeUntil} from 'rxjs/operators';
import {PricingModel} from '@iw-shop/article/model/pricing.model';
import {getSelectedCustomer} from '@store/selectors/user.selector';
import {ConfigService} from "@iw-shop/config/service/config.service";
import {UserService} from "@iw-shop/user/services/user.service";
import {PlatformService} from "@iw-shop/core/services/platform-service";
import {isPlatformBrowser} from "@angular/common";
import {ApiConfig} from "@api/configs";

@Component({
    selector: 'iw-shop-article-price',
    standalone: false,
    templateUrl: './article-price.component.html',
    styleUrls: ['./article-price.component.scss']
})
export class ArticlePriceComponent implements OnDestroy, OnInit, OnChanges {
    @Output() getPrice: EventEmitter<ApiArticlePrice> = new EventEmitter();

    @Input() article!: ApiArticle;
    @Input() basicPrice: boolean = true;

    destroy$: Subject<boolean> = new Subject<boolean>();

    customerId: string = '';
    customerPriceMode: 'net' | 'gross' = 'gross';
    pricingModel: PricingModel;

    priceOverrideText?: string;


    constructor(public articleService: ArticleService, private store: Store<AppState>, public configService: ConfigService, public userService: UserService, public platformService: PlatformService) {
        this.pricingModel = new PricingModel(this.articleService);

        this.store.select(getSelectedCustomer)
            .pipe(
                takeUntil(this.destroy$)
            )
            .subscribe(
                r => {
                    if (r?.id) {
                        this.customerId = r.id;
                        this.pricingModel.priceMode = r.priceMode ?? 'gross';
                        this.customerPriceMode = r.priceMode ?? 'gross';
                    }
                }
            )
    }

    ngOnChanges() {
        this.setPrices();
    }

    ngOnInit() {
        this.setPrices();
        this.checkAvailabilityRestrictions();
    }

    private checkAvailabilityRestrictions(): void {
        // Kombiniert Config und Customer-Daten, um die Sperr-Logik auszuführen
        combineLatest([
            this.store.select(state => state.config).pipe(take(1)),
            this.store.select(getSelectedCustomer).pipe(take(1))
        ]).subscribe(([config, customer]) => {
            const priceGroup = customer?.priceGroup || config.defaultPriceGroup || '';
            const availability = this.articleService.getAvailability(
                this.article.inventory || 0,
                this.article.availability
            );
            config.disableOrderForArticles?.atAvailabilities?.some(r => {
                if (r.availability === availability &&
                    (!r.requiredPriceGroups?.length || r.requiredPriceGroups.includes(priceGroup)) &&
                    ( (r.priceOverrideText?.length ?? 0) > 0)
                ) {
                    this.priceOverrideText = r.priceOverrideText;
                }
            });

        });
    }

    setPrices() {
        this.pricingModel.article = this.article;
        this.pricingModel.setPrices();
        if (this.pricingModel.offerPrice) {
            this.getPrice.emit(this.pricingModel.offerPrice);
        } else {
            this.getPrice.emit(this.pricingModel.price);
        }
    }

    isQuantityDiscount(quantityPrice: number, price: number): boolean {
        quantityPrice = Math.round(quantityPrice * 100) / 100;
        price = Math.round(price * 100) / 100;
        if (quantityPrice < price) {
            return true;
        }
        return false;

    }

    ngOnDestroy() {
        this.destroy$.next(true);
        this.destroy$.unsubscribe();
    }

    protected readonly isPlatformBrowser = isPlatformBrowser;
}
